import React, { useEffect, useMemo, useState } from 'react';
import { formatCheckId, statusBadgeClass, WIZARD_LAYERS } from './readinessUtils';
import './ReadinessRunProgress.css';

const LAYER_TITLES = WIZARD_LAYERS.reduce((acc, layer) => {
  acc[layer.layer] = layer.title;
  return acc;
}, {});

const STATUS_ICON = {
  pass: '✓',
  fail: '✗',
  error: '!',
  skip: '–',
  running: '…',
};

function layerLabel(layer) {
  const title = LAYER_TITLES[Number(layer)];
  return title ? `Layer ${layer} · ${title}` : `Layer ${layer}`;
}

function useElapsedLabel(startedAt, running) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const startMs = startedAt ? new Date(startedAt).getTime() : NaN;
  if (Number.isNaN(startMs)) return '';
  const seconds = Math.max(0, Math.floor((now - startMs) / 1000));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function ReadinessRunProgress({ startedAt, progress, running = true, title = 'Running readiness assessment' }) {
  const elapsed = useElapsedLabel(startedAt, running);

  const { total, results, current, pending, percent } = useMemo(() => {
    const planned = Array.isArray(progress?.planned) ? progress.planned : [];
    const done = Array.isArray(progress?.results) ? progress.results : [];
    const totalCount = Number(progress?.total || planned.length || 0);
    const doneIds = new Set(done.map((item) => item.id));
    const cur = progress?.current && !doneIds.has(progress.current.id) ? progress.current : null;
    const pendingChecks = planned.filter((item) => !doneIds.has(item.id) && item.id !== cur?.id);
    const pct = totalCount > 0 ? Math.min(100, Math.round((done.length / totalCount) * 100)) : 0;
    return { total: totalCount, results: done, current: cur, pending: pendingChecks, percent: pct };
  }, [progress]);

  const completed = results.length;

  return (
    <div className="card border-primary-subtle mb-3" role="status" aria-live="polite">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
            <span className="fw-semibold">{title}</span>
          </div>
          <div className="text-muted small font-monospace">
            {total ? `${completed} / ${total} checks` : 'Starting…'}
            {elapsed ? ` · ${elapsed}` : ''}
          </div>
        </div>

        <div className="progress mb-3" style={{ height: '0.6rem' }} aria-label="Assessment progress">
          <div
            className={`progress-bar progress-bar-striped progress-bar-animated ${total ? 'bg-primary' : 'bg-primary-subtle'}`}
            role="progressbar"
            style={{ width: total ? `${percent}%` : '100%' }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>

        {current ? (
          <div className="readiness-progress-current d-flex align-items-center gap-2 rounded bg-light px-2 py-2 mb-3">
            <span className="spinner-grow spinner-grow-sm text-primary readiness-progress-icon" aria-hidden="true" />
            <div className="flex-grow-1">
              <div className="fw-semibold small">{formatCheckId(current.id)}</div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {layerLabel(current.layer)}
              </div>
            </div>
            <span className="badge text-bg-primary">Running</span>
          </div>
        ) : null}

        {completed || pending.length ? (
          <ul className="list-group list-group-flush readiness-progress-list">
            {results.map((check) => (
              <li
                key={check.id}
                className="list-group-item readiness-progress-item d-flex align-items-center gap-2 px-2"
              >
                <span className={`badge readiness-progress-icon ${statusBadgeClass(check.status, check.severity)}`}>
                  {STATUS_ICON[check.status] || '•'}
                </span>
                <span className="flex-grow-1 small text-truncate" title={check.message || ''}>
                  {formatCheckId(check.id)}
                </span>
                <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                  {check.status}
                </span>
              </li>
            ))}
            {pending.map((check) => (
              <li
                key={check.id}
                className="list-group-item readiness-progress-pending d-flex align-items-center gap-2 px-2"
              >
                <span className="readiness-progress-icon text-muted">○</span>
                <span className="flex-grow-1 small text-truncate">{formatCheckId(check.id)}</span>
                <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                  queued
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-muted small">Preparing checks…</div>
        )}
      </div>
    </div>
  );
}
